// Pricing Section com Toggle e Planos
import { useState } from 'react';
import { pricingPlans } from '../data/pricingPlans';
import type { PricingPlan } from '../data/pricingPlans';
import { formatCurrency } from '../../../utils/formatters';
import { cn } from '../../../utils/cn';
import { CheckoutModal } from './CheckoutFlow/CheckoutModal';

export function Pricing() {
  const [isAnnual, setIsAnnual] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PricingPlan | null>(null);
  const [checkoutMode, setCheckoutMode] = useState<'start' | 'sales'>('start');

  const handlePlanClick = (plan: PricingPlan, mode: 'start' | 'sales') => {
    setSelectedPlan(plan);
    setCheckoutMode(mode);
    setIsModalOpen(true);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="pricing" className="py-20 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Toggle Mensal/Anual */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => setIsAnnual(false)}
              className={cn(
                'px-6 py-2 rounded-md font-medium transition-colors',
                !isAnnual
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              )}
            >
              Mensal
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              className={cn(
                'px-6 py-2 rounded-md font-medium transition-colors',
                isAnnual
                  ? 'bg-green-600 text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              )}
            >
              Anual (economize até R$1.300)
            </button>
          </div>
        </div>

        {/* Cards de Planos */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-12">
          {pricingPlans.map((plan) => (
            <div
              key={plan.id}
              className={cn(
                'rounded-xl p-6 border-2 transition-all duration-300',
                plan.highlighted
                  ? 'bg-green-800 text-white border-green-800 shadow-2xl transform scale-105'
                  : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-200 dark:border-gray-700 hover:shadow-lg'
              )}
            >
              {/* Header do Card */}
              <div
                className={cn(
                  'mb-4 pb-4 border-b-2',
                  plan.highlighted ? 'border-green-700' : 'border-gray-200 dark:border-gray-700'
                )}
              >
                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <p className={cn('text-sm mb-4', plan.highlighted ? 'text-green-100' : 'text-gray-600 dark:text-gray-300')}>
                  {plan.description}
                </p>
                <div className="mb-4">
                  <span className="text-3xl font-bold">
                    {formatCurrency(isAnnual ? plan.annualPrice / 12 : plan.monthlyPrice)}
                  </span>
                  <span className={cn('text-sm ml-2', plan.highlighted ? 'text-green-200' : 'text-gray-500 dark:text-gray-400')}>
                    /mês
                  </span>
                </div>
                <button
                  onClick={() => handlePlanClick(plan, plan.ctaType === 'free' ? 'start' : 'sales')}
                  className={cn(
                    'w-full py-2 px-4 rounded-lg font-medium transition-colors',
                    plan.ctaType === 'free'
                      ? plan.highlighted
                        ? 'bg-green-700 hover:bg-green-600 text-white'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                      : plan.highlighted
                        ? 'bg-green-700 hover:bg-green-600 text-white'
                        : 'bg-green-700 hover:bg-green-800 text-white'
                  )}
                >
                  {plan.ctaType === 'free' ? 'Comece grátis' : 'Fale com vendas'}
                </button>
              </div>

              {/* Usuários */}
              <div className={cn('flex items-center gap-2 mb-4', plan.highlighted ? 'text-green-100' : 'text-gray-600 dark:text-gray-300')}>
                <span className="text-sm">👤</span>
                <span className="text-sm">{plan.users} usuários</span>
              </div>
            </div>
          ))}
        </div>

        {/* Botão de Comparar */}
        <div className="text-center">
          <button
            onClick={() => scrollToSection('pricing-details')}
            className="px-8 py-4 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 mx-auto"
          >
            Compare todos os recursos dos planos
            <span className="text-xl">↓</span>
          </button>
        </div>
      </div>

      {selectedPlan && (
        <CheckoutModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          plan={selectedPlan}
          initialMode={checkoutMode}
        />
      )}
    </section>
  );
}
